import React, { Component } from 'react';
import { WidgetHelper } from '../WidgetHelper';
import { IEmployerFilterParams, IEmployerLight } from '../../Models';
import { EmployerService } from '../../Services';
import { EmployersFilter } from '../../Components';
import { EmployerArticle } from './EmployerArticle';

interface IEmployersListWidgetState {
    employersLabeledList: Record<string, IEmployerLight[]>;
    employersFilterParams: IEmployerFilterParams;
    isloading: boolean;
}

interface IEmployersListWidgetProps {
    description: string;
    introduction: string;
    employerDetailPage: string;
    homepageUrl: string;
    dictEmployersTitle: string;
    dictEmployersLabelYear: string;
    dictSearch: string;
    dictSearchEmployer: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictEmployersWithOffers: string;
    dictOtherEmployers: string;
}

export class EmployersLabeledListWidget extends Component<
    IEmployersListWidgetProps,
    IEmployersListWidgetState
> {
    private cancellationToken: AbortController | null = null;

    constructor(props: IEmployersListWidgetProps) {
        super(props);

        this.state = {
            employersLabeledList: {},
            employersFilterParams: {
                name: '',
                page: 0,
                labeledYears: this.getLabeledYears(),
            },
            isloading: true,
        };
    }

    componentDidMount() {
        void this.getEmployerList(this.state.employersFilterParams);
    }

    componentWillUnmount() {
        if (this.cancellationToken) {
            this.cancellationToken.abort();
        }
    }

    private getCurrentYear() {
        return new Date().getFullYear();
    }

    private getLabeledYears() {
        const year = this.getCurrentYear();
        return [year.toString(), (year - 1).toString(), (year - 2).toString()];
    }

    private getEmployerList = async (
        searchParams: IEmployerFilterParams = this.state.employersFilterParams
    ) => {
        if (this.cancellationToken) {
            this.cancellationToken.abort();
        }

        this.cancellationToken = new AbortController();
        try {
            const employersLabeledList =
                await EmployerService.getLabelisedEmployers(
                    searchParams,
                    this.cancellationToken.signal
                );

            this.setState((prev) => ({
                employersLabeledList,
                employersFilterParams: {
                    ...prev.employersFilterParams,
                    page: prev.employersFilterParams.page + 1,
                },
                isloading: false,
            }));
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    };

    // ✔ Now bound correctly
    private updateEmployersFilterParams = (
        employersFilterParams: IEmployerFilterParams
    ) => {
        this.setState({ employersFilterParams });
        void this.getEmployerList(employersFilterParams);
    };

    render() {
        const { employersLabeledList } = this.state;

        return (
            <>
                <div className="banner banner--colored banner--colored__employers">
                    <EmployersFilter
                        filterEmployersList={this.updateEmployersFilterParams}
                        labeledYears={this.getLabeledYears()}
                        employersTitle={this.props.dictEmployersTitle}
                        search={this.props.dictSearch}
                        searchEmployer={this.props.dictSearchEmployer}
                    />
                </div>

                <div className="container">
                    <div className="row intro no-gutters">
                        <div className="col-12">
                            <p>{this.props.description}</p>
                        </div>
                        {this.props.introduction && (
                            <div className="col-12" data-test="intro">
                                <p>{this.props.introduction}</p>
                            </div>
                        )}
                    </div>
                </div>

                {Object.keys(employersLabeledList).length > 0 && (
                    <div>
                        {Object.entries(this.state.employersLabeledList)
                            .reverse()
                            .map(([year, employers]) => {
                                if (
                                    !Array.isArray(employers) ||
                                    employers.length === 0
                                ) {
                                    return null;
                                }

                                return (
                                    <section
                                        key={year}
                                        className="highlight-section"
                                        style={{
                                            backgroundColor:
                                                this.getCurrentYear() ===
                                                Number(year)
                                                    ? '#8fdcc3'
                                                    : 'white',
                                        }}
                                    >
                                        <div className="container">
                                            <h2>
                                                {
                                                    this.props
                                                        .dictEmployersLabelYear
                                                }{' '}
                                                {year}
                                            </h2>

                                            <div className="row cards">
                                                {employers
                                                    .sort((a, b) =>
                                                        a.name.localeCompare(
                                                            b.name
                                                        )
                                                    )
                                                    .map(
                                                        (
                                                            employer: IEmployerLight
                                                        ) => (
                                                            <EmployerArticle
                                                                key={
                                                                    employer.id
                                                                }
                                                                {...employer}
                                                                homepageUrl={
                                                                    this.props
                                                                        .homepageUrl
                                                                }
                                                                employerDetailPage={
                                                                    this.props
                                                                        .employerDetailPage
                                                                }
                                                            />
                                                        )
                                                    )}
                                            </div>
                                        </div>
                                    </section>
                                );
                            })}
                    </div>
                )}
            </>
        );
    }
}

WidgetHelper.register(
    EmployersLabeledListWidget,
    'employers-labeled-list-widget'
);
